/**
 * Normalization for raw assistant message text, shared by the on-screen
 * renderer (AssistantBox) and the PDF exporter so both resolve citations and
 * case tags identically.
 *
 * Extracted from AssistantBox; what used to be closure variables there
 * (`allReferences`, `canLinkCaseCitations`) are parameters here.
 */

import { removeChatSocReferences, removeLegalDraftContent } from './legalArtifacts';
import { getTrustedCaseUrl } from './urlSafety';

export interface PrepareTextOptions {
    /**
     * Render the VERIFIED/UNVERIFIED badges and case-name hyperlinks. Off for
     * plain-text destinations such as the clipboard.
     */
    renderCaseCitationControls?: boolean;
    /** Whether verified case names may link out to the precedent PDF. */
    canLinkCaseCitations?: boolean;
}

export function escapeHtmlAttribute(value: string) {
    return value
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

export function escapeHtml(value: string) {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

export function getSafeCaseUrl(url: string | undefined) {
    return getTrustedCaseUrl(url);
}

export function normalizeInlineCaseCitation(value: string) {
    return value
        .replace(/\*\*([^*]+)\*\*/g, '$1')
        .replace(/\*([^*]+)\*/g, '$1')
        .replace(/__([^_]+)__/g, '$1')
        .replace(/_([^_]+)_/g, '$1')
        .replace(/`([^`]+)`/g, '$1')
        .replace(/\s+/g, ' ')
        .trim();
}

export function splitCaseCitation(citation: string): { caseName: string; citationTail: string } {
    const normalized = normalizeInlineCaseCitation(citation);
    const citationStart = normalized.search(
        /\s+(?=(?:\(\d{4}\)\s*)?(?:\d+\s+CCC\s+\d+|\d{4}\s+Cal\.\s+Wrk\.\s+Comp\.\s+(?:P\.D\.\s+)?LEXIS\s+\d+|WCAB\s+Panel\s+Decision))/i,
    );

    if (citationStart > 0) {
        return {
            caseName: normalized.slice(0, citationStart).replace(/[,\s]+$/, ''),
            citationTail: normalized.slice(citationStart).trim(),
        };
    }

    return {
        caseName: normalized,
        citationTail: '',
    };
}

export function buildCaseCitationHtml(
    citation: string,
    status: string | null,
    safeUrl?: string,
    canLinkCaseCitations = false,
) {
    const normalizedCitation = normalizeInlineCaseCitation(citation);
    const shouldLinkCaseName = status === 'verified' && canLinkCaseCitations && !!safeUrl;
    const { caseName, citationTail } = splitCaseCitation(citation);

    const citationHtml = shouldLinkCaseName && caseName
        ? [
            `<a href="${escapeHtmlAttribute(safeUrl || '')}" data-case-citation-link="1" target="_blank" rel="noopener noreferrer">${escapeHtml(caseName)}</a>`,
            citationTail ? ` ${escapeHtml(citationTail)}` : '',
        ].join('')
        : escapeHtml(normalizedCitation);

    if (status === 'verified') {
        const urlAttr = shouldLinkCaseName && safeUrl ? ` data-url="${escapeHtmlAttribute(safeUrl)}"` : '';
        return `${citationHtml} <span data-verified="1"${urlAttr}>VERIFIED</span>`;
    }

    if (status === 'failed') {
        return `${citationHtml} <span data-failed="1">UNVERIFIED</span>`;
    }

    return citationHtml;
}

/**
 * CommonMark forbids unescaped spaces in a link destination, so a citation whose
 * chapter contains spaces (e.g. `[SOC Section Glossary of Terms](.../Glossary of
 * Terms?anchor=...)`) never parses as a link and leaks into the answer as literal
 * text. Percent-encode those spaces so the destination is well-formed.
 */
export function normalizeLinkDestinations(text: string) {
    // ](<url> "optional title") — the optional title keeps its spaces.
    return text.replace(
        /\]\(\s*(https?:\/\/[^)\n]*?)\s*(\s+"[^"\n]*")?\)/g,
        (_match, url: string, title: string | undefined) =>
            `](${url.replace(/ /g, '%20')}${title || ''})`
    );
}

export function getLabelFromUrl(url: string | undefined, allReferences: any) {
    if (url && Array.isArray(allReferences)) {
        const anchor = url.split('?anchor=')[1];
        if (!anchor) return;
        const ref = allReferences.find((ref: any) => ref && ref.url_anchor === anchor);
        if (ref) {
            return `${ref.title} ${ref.subtitle ? '(' + ref.subtitle + (ref.heading ? ': ' + ref.heading : '') + ') ' : ''}`;
        }
    }
    return;
}

export function prepareText(
    text: string,
    showSources: boolean,
    allReferences: any,
    options: PrepareTextOptions = {},
) {
    const { renderCaseCitationControls = true, canLinkCaseCitations = false } = options;

    text = text.replace(/\\n/g, "\n");
    if (text.charAt(0) === '"' && text.charAt(text.length - 1) === '"') {
        text = text.slice(1, -1);
    }
    text = removeLegalDraftContent(text);

    const refRegex = /[【［[]((?:\d+(?:,\s*\d+)*))[】］\]]/g;

    // Replace complete case citations with special HTML span wrappers for parsing with rehype-raw
    text = text.replace(/<<case([^>]*)>>([\s\S]*?)<<\/case>>/g, (match, attrs, citation) => {
        const statusMatch = attrs.match(/status="([^"]+)"/);
        const status = statusMatch ? statusMatch[1] : null;
        const urlMatch = attrs.match(/url="([^"]+)"/);
        const safeUrl = getSafeCaseUrl(urlMatch ? urlMatch[1] : undefined);
        if (!renderCaseCitationControls) return normalizeInlineCaseCitation(citation);
        return buildCaseCitationHtml(citation, status, safeUrl, canLinkCaseCitations);
    });

    // Strip out any incomplete or leftover case tags (e.g., during streaming)
    text = text.replace(/<<case[^>]*>>/g, '');
    text = text.replace(/<</g, '').replace(/>>/g, '');

    text = text.replace(refRegex, (match: any, numbers: string) => {
        const indices = numbers.split(',').map(num => parseInt(num.trim()) - 1);

        const processedRefs = indices.map(idx => {
            if (allReferences && Array.isArray(allReferences) && idx >= 0 && idx < allReferences.length) {
                let url = '';
                let section = allReferences[idx].title ? allReferences[idx].title : allReferences[idx];

                if (/\d/.test(section.split(' ')[0])) {
                    section = section.split(' ')[0];
                    url = allReferences[idx].title
                        ? `https://app.sullivanoncomp.com/soc/index/title/${section}?anchor=${allReferences[idx].url_anchor}`
                        : `https://app.sullivanoncomp.com/soc/index/title/${section}`;
                    return ` [[SOC Section ${section}]](${url})`;
                } else {
                    if (allReferences[idx].url_anchor && allReferences[idx].url_anchor.toLowerCase().includes('glossary')) {
                        url = `https://app.sullivanoncomp.com/glossary`;
                    } else {
                        url = `https://app.sullivanoncomp.com/calculators/${allReferences[idx].url_anchor}`;
                        return ` [[SOC ${section} Calculator]](${url})`;
                    }
                    return ` [[SOC ${section}]](${url})`;
                }
            }
            return '';
        }).filter(ref => ref !== '');

        if (!showSources) {
            return '';
        }

        return processedRefs.join(' ');
    });

    if (!showSources) {
        text = removeChatSocReferences(text);
    }

    return normalizeLinkDestinations(text);
}
